import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import ContextMenu from './ContextMenu';

const SORT_OPTIONS = [
  { value: 'updated', label: '수정일' },
  { value: 'created', label: '생성일' },
  { value: 'title', label: '제목' },
];

const TAG_COLORS = [
  '#3B82F6', '#EF4444', '#22C55E', '#F59E0B', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#64748B',
];

function buildTagTree(tagCounts) {
  const tree = {};
  tagCounts.forEach(([tag, count]) => {
    const parts = tag.split('/');
    let current = tree;
    parts.forEach((part, i) => {
      const fullPath = parts.slice(0, i + 1).join('/');
      if (!current[part]) {
        current[part] = { fullPath, count: 0, children: {} };
      }
      if (fullPath === tag) current[part].count = count;
      current = current[part].children;
    });
  });
  return tree;
}

// ─── Note Item with floating tooltip ───
const SidebarNoteItem = ({ doc, depth, activeId, selectedIds, childrenMap, collapsedDocs,
  trashMode, onClick, onContextMenu, onToggleCollapse, formatDate, getPreview }) => {
  const [showTip, setShowTip] = useState(false);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });
  const timerRef = useRef(null);
  const itemRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const isActive = activeId === doc.id;
  const isSelected = selectedIds?.has(doc.id);
  const children = childrenMap[doc.id] || [];
  const isCollapsed = collapsedDocs.has(doc.id);

  const onEnter = () => {
    // No tooltips on touch/mobile
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
    timerRef.current = setTimeout(() => {
      if (itemRef.current) {
        const rect = itemRef.current.getBoundingClientRect();
        const spaceRight = window.innerWidth - rect.right;
        let x = spaceRight > 260 ? rect.right + 8 : rect.left;
        let y = spaceRight > 260 ? rect.top : rect.bottom + 4;
        // Clamp to viewport bottom
        const tipHeight = 160;
        if (y + tipHeight > window.innerHeight - 8) y = window.innerHeight - tipHeight - 8;
        if (y < 8) y = 8;
        setTipPos({ x, y });
        setShowTip(true);
      }
    }, 400);
  };
  const onLeave = () => { clearTimeout(timerRef.current); setShowTip(false); };

  const preview = getPreview(doc.content);
  const date = formatDate(trashMode ? doc.deleted_at : doc.updated_at);
  const created = formatDate(doc.created_at);
  const words = doc.content ? doc.content.trim().split(/\s+/).length : 0;
  let tags = [];
  if (Array.isArray(doc.tags)) tags = doc.tags;
  else if (typeof doc.tags === 'string') { try { tags = JSON.parse(doc.tags); } catch {} }

  return (
    <React.Fragment>
      <div ref={itemRef}
        onClick={(e) => onClick(e, doc.id)}
        onContextMenu={(e) => onContextMenu(e, doc)}
        onMouseEnter={onEnter} onMouseLeave={onLeave}
        className={`bear-note-item ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''}`}
        style={{ paddingLeft: `${12 + depth * 16}px` }}
      >
        <div className="bear-note-title">
          {children.length > 0 && (
            <span className="bear-tree-toggle" onClick={(e) => onToggleCollapse(doc.id, e)}
              title={isCollapsed ? '펼치기' : '접기'}>{isCollapsed ? '▶' : '▼'}</span>
          )}
          {depth > 0 && children.length === 0 && <span className="bear-tree-spacer" />}
          {!trashMode && doc.is_pinned && <span className="bear-pin-icon">📌 </span>}
          {doc.title || '제목 없음'}
        </div>
      </div>
      {showTip && (
        <div className="bear-note-tooltip" style={{ left: tipPos.x, top: tipPos.y }}>
          <div className="bear-note-tooltip-title">{doc.title || '제목 없음'}</div>
          <div className="bear-note-tooltip-preview">{preview}</div>
          <div className="bear-note-tooltip-meta">
            <span>수정: {date}</span><span>생성: {created}</span><span>{words}단어</span>
          </div>
          {tags.length > 0 && (
            <div className="bear-note-tooltip-tags">
              {tags.map((t, i) => <span key={i} className="bear-note-tooltip-tag">#{t}</span>)}
            </div>
          )}
        </div>
      )}
      {!isCollapsed && children.length > 0 && children.map(child => (
        <SidebarNoteItem key={child.id} doc={child} depth={depth + 1}
          activeId={activeId} selectedIds={selectedIds} childrenMap={childrenMap}
          collapsedDocs={collapsedDocs} trashMode={trashMode}
          onClick={onClick} onContextMenu={onContextMenu}
          onToggleCollapse={onToggleCollapse}
          formatDate={formatDate} getPreview={getPreview} />
      ))}
    </React.Fragment>
  );
};

const Sidebar = ({
  documents,
  loading,
  search,
  onSearchChange,
  activeId,
  onSelect,
  onNew,
  onNewChild,
  onDelete,
  onPin,
  onDuplicate,
  onExport,
  onRestore,
  onPermanentDelete,
  onEmptyTrash,
  isOpen,
  onToggle,
  allDocuments,
  activeTag,
  onTagSelect,
  selectedIds,
  onToggleSelect,
  onRangeSelect,
  onClearSelection,
  sortBy,
  sortOrder,
  onSortChange,
  trashMode,
  onTrashToggle,
  trashCount,
  onMerge,
  onMove,
  // Tag management
  onTagRename,
  onTagDelete,
  onTagMerge,
  onTagColor,
  tagMetadata,
  // Project docs
  projectDocs,
  onProjectDocSelect,
  activeProjectDoc,
  // Quick Open
  onQuickOpen,
}) => {
  const [contextMenu, setContextMenu] = useState(null);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [collapsedTags, setCollapsedTags] = useState(new Set());
  const [collapsedDocs, setCollapsedDocs] = useState(new Set());

  // Read doc IDs for NEW badge (persisted in localStorage)
  const [readDocIds, setReadDocIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('mb_readDocIds') || '[]')); } catch { return new Set(); }
  });
  const markDocRead = useCallback((docId) => {
    setReadDocIds(prev => {
      if (prev.has(docId)) return prev;
      const next = new Set(prev);
      next.add(docId);
      localStorage.setItem('mb_readDocIds', JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Blog (notes) section - always expanded (flex:1 absorbs space changes)

  // Tag section state
  const [tagSectionCollapsed, setTagSectionCollapsed] = useState(() => {
    const saved = localStorage.getItem('mb_tagSectionCollapsed');
    return saved !== null ? saved === 'true' : true;
  });
  const [tagSearch, setTagSearch] = useState('');
  const [tagSortBy, setTagSortBy] = useState(() => localStorage.getItem('mb_tagSortBy') || 'count');

  // Tag section height (resizable)
  const [tagHeight, setTagHeight] = useState(() => {
    const saved = localStorage.getItem('mb_tagHeight');
    return saved ? parseInt(saved, 10) : 160;
  });
  const resizeRef = useRef(null);

  // Project docs section state
  const [projectSectionCollapsed, setProjectSectionCollapsed] = useState(() => {
    const saved = localStorage.getItem('mb_projectSectionCollapsed');
    return saved !== null ? saved === 'true' : true;
  });
  const [expandedProjects, setExpandedProjects] = useState(new Set());

  // Project section height (resizable)
  const [projectHeight, setProjectHeight] = useState(() => {
    const saved = localStorage.getItem('mb_projectHeight');
    return saved ? parseInt(saved, 10) : 200;
  });
  const projectResizeRef = useRef(null);

  useEffect(() => { localStorage.setItem('mb_projectSectionCollapsed', String(projectSectionCollapsed)); }, [projectSectionCollapsed]);

  const toggleProjectExpand = useCallback((projectName) => {
    setExpandedProjects(prev => {
      const n = new Set(prev);
      n.has(projectName) ? n.delete(projectName) : n.add(projectName);
      return n;
    });
  }, []);

  // Status dot tooltips
  const STATUS_TOOLTIPS = {
    ok: '✅ 동기화 완료, 이상 없음',
    changed: '🟡 변경사항 있음 (TODO/수정 필요)',
    review: '🔵 QA 및 코드 검수 필요',
    wip: '🔴 진행중 / 작업 미완료',
    unknown: '⚫ 내용 누락 또는 확인 필요',
  };

  // Auto-scroll to active project doc
  const projectSectionRef = useRef(null);
  useEffect(() => {
    if (!activeProjectDoc?.project || !activeProjectDoc?.id) return;
    if (!projectDocs || projectDocs.length === 0) return;

    // Find which machine group this project belongs to
    const MACBOOK_LIST = ['dev-workspace', 'lmStudio'];
    const MINI_LIST = ['Myproject-mini', 'ilouli-opentina', 'ilouli-openclaw', 'MemoryStorage'];
    let groupKey = 'dev';
    if (MACBOOK_LIST.includes(activeProjectDoc.project)) groupKey = 'macbook';
    else if (MINI_LIST.includes(activeProjectDoc.project)) groupKey = 'mini';

    // Expand section, group, and project
    setProjectSectionCollapsed(false);
    setExpandedProjects(prev => {
      const n = new Set(prev);
      n.add(`__group__${groupKey}`);
      n.add(activeProjectDoc.project);
      return n;
    });

    // Scroll into view after DOM update
    requestAnimationFrame(() => {
      setTimeout(() => {
        const activeEl = projectSectionRef.current?.querySelector('.bear-project-doc.active');
        if (activeEl) {
          activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 100);
    });
  }, [activeProjectDoc?.project, activeProjectDoc?.id, projectDocs]);

  // Tag dialogs
  const [renameDialog, setRenameDialog] = useState(null);
  const renameInputRef = useRef(null);
  const [colorPicker, setColorPicker] = useState(null);
  const [mergeDialog, setMergeDialog] = useState(null);
  const [mergeTarget, setMergeTarget] = useState('');

  useEffect(() => { localStorage.setItem('mb_tagSectionCollapsed', String(tagSectionCollapsed)); }, [tagSectionCollapsed]);
  useEffect(() => { localStorage.setItem('mb_tagSortBy', tagSortBy); }, [tagSortBy]);
  useEffect(() => { localStorage.setItem('mb_tagHeight', String(tagHeight)); }, [tagHeight]);
  useEffect(() => { localStorage.setItem('mb_projectHeight', String(projectHeight)); }, [projectHeight]);

  const [dragging, setDragging] = useState(false);

  const handleProjectResizeStart = useCallback((e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = projectHeight;
    const maxH = window.innerHeight - 120;
    let moved = false;
    const onMove = (ev) => {
      if (!moved) {
        moved = true;
        setDragging(true);
      }
      const diff = startY - ev.clientY; // 핸들이 커서를 따라감
      setProjectHeight(Math.max(40, Math.min(maxH, startH + diff)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (moved) setDragging(false);
    };
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [projectHeight]);

  const handleProjectResizeDblClick = useCallback(() => {
    setProjectSectionCollapsed(prev => !prev);
  }, []);
  useEffect(() => { if (renameDialog && renameInputRef.current) { renameInputRef.current.focus(); renameInputRef.current.select(); } }, [renameDialog]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startTagH = tagHeight;
    const startProjH = projectHeight;
    let moved = false;
    const onMove = (ev) => {
      if (!moved) {
        moved = true;
        setDragging(true);
      }
      const diff = startY - ev.clientY; // 핸들이 커서를 따라감
      const newTagH = Math.max(40, startTagH + diff);
      const actualDiff = newTagH - startTagH;
      // 태그가 커지면 프로젝트가 줄어들고, 태그가 줄면 프로젝트가 커짐
      const newProjH = Math.max(40, startProjH - actualDiff);
      setTagHeight(newTagH);
      setProjectHeight(newProjH);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (moved) setDragging(false);
    };
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [tagHeight, projectHeight]);

  const handleTagResizeDblClick = useCallback(() => {
    setTagSectionCollapsed(prev => !prev);
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const days = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (days === 0) return date.toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit', hour12: false });
    if (days === 1) return '어제';
    if (days < 7) return `${days}일 전`;
    return date.toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul', month: 'short', day: 'numeric' });
  };

  const getPreview = (content) => {
    if (!content) return '내용 없음';
    const plain = content.replace(/[#*`>\[\]()_~\-]/g, '').replace(/\n+/g, ' ').trim();
    return plain.substring(0, 80) || '내용 없음';
  };

  // Tag counts + tree
  const { tagTree, tagFlat, untaggedCount, pinnedCount, totalNotes } = useMemo(() => {
    const docs = allDocuments || documents;
    const counts = {};
    let untagged = 0, pinned = 0;
    docs.forEach(doc => {
      let docTags = [];
      if (Array.isArray(doc.tags)) docTags = doc.tags;
      else if (typeof doc.tags === 'string') { try { docTags = JSON.parse(doc.tags); } catch { docTags = []; } }
      if (docTags.length === 0) untagged++;
      if (doc.is_pinned) pinned++;
      docTags.forEach(tag => { counts[tag] = (counts[tag] || 0) + 1; });
    });
    let sorted = tagSortBy === 'name'
      ? Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0]))
      : Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const filtered = tagSearch ? sorted.filter(([tag]) => tag.toLowerCase().includes(tagSearch.toLowerCase())) : sorted;
    return { tagTree: buildTagTree(filtered), tagFlat: filtered, untaggedCount: untagged, pinnedCount: pinned, totalNotes: docs.length };
  }, [allDocuments, documents, tagSortBy, tagSearch]);

  // Document tree
  const { rootDocs, childrenMap } = useMemo(() => {
    if (trashMode) return { rootDocs: documents, childrenMap: {} };
    const map = {};
    documents.forEach(doc => { const pid = doc.parent_id || null; if (!map[pid]) map[pid] = []; map[pid].push(doc); });
    return { rootDocs: map[null] || [], childrenMap: map };
  }, [documents, trashMode]);

  const { pinnedDocs, unpinnedDocs } = useMemo(() => {
    if (trashMode) return { pinnedDocs: [], unpinnedDocs: documents };
    return { pinnedDocs: rootDocs.filter(d => d.is_pinned), unpinnedDocs: rootDocs.filter(d => !d.is_pinned) };
  }, [rootDocs, documents, trashMode]);

  const getTagColor = useCallback((tagPath) => {
    if (!tagMetadata) return null;
    return tagMetadata.find(m => m.name === tagPath)?.color || null;
  }, [tagMetadata]);

  // Click handlers
  const handleClick = useCallback((e, docId) => {
    if (e.metaKey || e.ctrlKey) { e.preventDefault(); onToggleSelect?.(docId); }
    else if (e.shiftKey) { e.preventDefault(); onRangeSelect?.(docId); }
    else { onClearSelection?.(); onSelect(docId); markDocRead(docId); }
  }, [onSelect, onToggleSelect, onRangeSelect, onClearSelection, markDocRead]);

  const handleContextMenu = useCallback((e, doc) => {
    e.preventDefault();
    const isSelected = selectedIds?.has(doc.id);
    const multiSelected = selectedIds?.size > 1;
    const hasChildDocs = childrenMap[doc.id]?.length > 0;
    const items = trashMode ? [
      { icon: '↩', label: '복원', onClick: () => onRestore?.(doc.id) },
      { divider: true },
      { icon: '⚠', label: '영구 삭제', onClick: () => onPermanentDelete?.(doc.id), danger: true },
    ] : [
      { icon: '📄', label: '하위 노트 추가', onClick: () => onNewChild?.(doc.id) },
      ...(doc.parent_id ? [{ icon: '⬆', label: '최상위로 이동', onClick: () => onMove?.(doc.id, null) }] : []),
      { divider: true },
      { icon: doc.is_pinned ? '📌' : '📍', label: doc.is_pinned ? '고정 해제' : '상단에 고정', onClick: () => onPin?.(doc.id) },
      { icon: '📋', label: '복제', onClick: () => onDuplicate?.(doc.id) },
      { icon: '📥', label: '.md로 내보내기', onClick: () => onExport?.(doc.id, doc.title) },
      ...(multiSelected && isSelected ? [{ divider: true }, { icon: '🔗', label: `${selectedIds.size}개 노트 합치기`, onClick: () => onMerge?.() }] : []),
      { divider: true },
      { icon: '🗑', label: hasChildDocs ? '삭제 (하위→최상위 이동)' : '삭제', onClick: () => onDelete(doc.id), danger: true },
    ];
    setContextMenu({ x: e.clientX, y: e.clientY, items });
  }, [selectedIds, trashMode, childrenMap, onPin, onDuplicate, onExport, onDelete, onRestore, onPermanentDelete, onMerge, onNewChild, onMove]);

  const handleTagContextMenu = useCallback((e, tagPath) => {
    e.preventDefault(); e.stopPropagation();
    const items = [
      { icon: '✏️', label: '이름 변경', onClick: () => setRenameDialog({ tag: tagPath, value: tagPath.split('/').pop() }) },
      { icon: '🎨', label: '색상 변경', onClick: () => setColorPicker({ tag: tagPath, x: e.clientX, y: e.clientY }) },
      { divider: true },
      { icon: '🔗', label: '다른 태그로 병합', onClick: () => { setMergeDialog({ sourceTag: tagPath }); setMergeTarget(''); } },
      { divider: true },
      { icon: '🗑', label: '태그 삭제', onClick: () => {
        if (window.confirm(`"${tagPath}" 태그를 삭제하시겠습니까? (문서는 유지)`)) onTagDelete?.(tagPath);
      }, danger: true },
    ];
    setContextMenu({ x: e.clientX, y: e.clientY, items });
  }, [onTagDelete]);

  const handleRenameSubmit = useCallback(() => {
    if (!renameDialog) return;
    const newName = renameDialog.value.trim();
    if (!newName || newName === renameDialog.tag.split('/').pop()) { setRenameDialog(null); return; }
    const parts = renameDialog.tag.split('/'); parts[parts.length - 1] = newName;
    onTagRename?.(renameDialog.tag, parts.join('/'));
    setRenameDialog(null);
  }, [renameDialog, onTagRename]);

  const handleMergeSubmit = useCallback(() => {
    if (!mergeDialog || !mergeTarget.trim()) return;
    onTagMerge?.(mergeDialog.sourceTag, mergeTarget.trim());
    setMergeDialog(null); setMergeTarget('');
  }, [mergeDialog, mergeTarget, onTagMerge]);

  const toggleDocCollapse = useCallback((docId, e) => {
    e.stopPropagation();
    setCollapsedDocs(prev => { const n = new Set(prev); n.has(docId) ? n.delete(docId) : n.add(docId); return n; });
  }, []);

  const toggleTagCollapse = useCallback((tagPath) => {
    setCollapsedTags(prev => { const n = new Set(prev); n.has(tagPath) ? n.delete(tagPath) : n.add(tagPath); return n; });
  }, []);

  // ─── Note Item with Tooltip (rendered via sub-component) ───
  const renderNoteItem = (doc, depth = 0) => (
    <SidebarNoteItem key={doc.id} doc={doc} depth={depth}
      activeId={activeId} selectedIds={selectedIds} childrenMap={childrenMap}
      collapsedDocs={collapsedDocs} trashMode={trashMode}
      onClick={handleClick} onContextMenu={handleContextMenu}
      onToggleCollapse={toggleDocCollapse}
      formatDate={formatDate} getPreview={getPreview}
    />
  );

  // Render tag node
  const renderTagNode = (node, name, depth = 0) => {
    const childKeys = Object.keys(node.children);
    const hasChildren = childKeys.length > 0;
    const isCollapsed = collapsedTags.has(node.fullPath);
    const isActive = activeTag === node.fullPath;
    const color = getTagColor(node.fullPath);
    return (
      <React.Fragment key={node.fullPath}>
        <div
          className={`bear-tag-item ${isActive ? 'active' : ''}`}
          style={{ paddingLeft: `${12 + depth * 14}px` }}
          onClick={() => onTagSelect(isActive ? null : node.fullPath)}
          onContextMenu={(e) => handleTagContextMenu(e, node.fullPath)}
        >
          <span className="bear-tag-name">
            {hasChildren && <span className="bear-tree-toggle" onClick={(e) => { e.stopPropagation(); toggleTagCollapse(node.fullPath); }}>{isCollapsed ? '▶' : '▼'}</span>}
            {!hasChildren && depth > 0 && <span className="bear-tree-spacer" />}
            {color && <span className="bear-tag-dot" style={{ background: color }} />}
            # {name}
          </span>
          {node.count > 0 && <span className="bear-tag-count">{node.count}</span>}
        </div>
        {hasChildren && !isCollapsed && childKeys.map(key => renderTagNode(node.children[key], key, depth + 1))}
      </React.Fragment>
    );
  };

  const hasSelection = selectedIds && selectedIds.size > 0;
  const totalTagCount = tagFlat.length;

  return (
    <div className="bear-sidebar">
      {/* Header */}
      <div className="bear-sidebar-header">
        <div className="bear-sidebar-title">
          <span className="bear-sidebar-label">{trashMode ? '휴지통' : '노트'}</span>
          <span className="bear-sidebar-count">{documents.length}</span>
        </div>
        <div className="bear-sidebar-actions">
          <button onClick={onToggle} className="bear-new-btn" data-tip="사이드바 닫기">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 19l-7-7 7-7M18 19l-7-7 7-7" />
            </svg>
          </button>
          {!trashMode && (
            <>
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowSortMenu(!showSortMenu)} className="bear-new-btn" data-tip="정렬">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M6 12h12M9 18h6" /></svg>
                </button>
                {showSortMenu && (
                  <div className="bear-sort-menu">
                    {SORT_OPTIONS.map(opt => (
                      <button key={opt.value} className={`bear-sort-item ${sortBy === opt.value ? 'active' : ''}`}
                        onClick={() => { onSortChange?.(opt.value, sortOrder); setShowSortMenu(false); }}>
                        {sortBy === opt.value && '✓ '}{opt.label}
                      </button>
                    ))}
                    <div className="bear-sort-divider" />
                    <button className={`bear-sort-item ${sortOrder === 'desc' ? 'active' : ''}`}
                      onClick={() => { onSortChange?.(sortBy, 'desc'); setShowSortMenu(false); }}>
                      {sortOrder === 'desc' && '✓ '}최신순
                    </button>
                    <button className={`bear-sort-item ${sortOrder === 'asc' ? 'active' : ''}`}
                      onClick={() => { onSortChange?.(sortBy, 'asc'); setShowSortMenu(false); }}>
                      {sortOrder === 'asc' && '✓ '}오래된순
                    </button>
                  </div>
                )}
              </div>
              <button onClick={() => {
                if (activeId) {
                  onNewChild?.(activeId);
                } else {
                  onNew();
                }
              }} className="bear-new-btn" data-tip="새 노트">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </>
          )}
          {trashMode && (
            <button onClick={onTrashToggle} className="bear-new-btn" data-tip="노트로 돌아가기">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            </button>
          )}
        </div>
      </div>

      {/* Selection Bar */}
      {hasSelection && !trashMode && (
        <div className="bear-selection-bar">
          <span className="bear-selection-count">{selectedIds.size}개 선택됨</span>
          <div className="bear-selection-actions">
            {selectedIds.size >= 2 && <button className="bear-selection-btn" onClick={onMerge} data-tip="합치기">🔗</button>}
            <button className="bear-selection-btn danger" onClick={() => {
              if (window.confirm(`${selectedIds.size}개 노트를 삭제하시겠습니까?`)) { selectedIds.forEach(id => onDelete(id)); onClearSelection?.(); }
            }} data-tip="삭제">🗑</button>
            <button className="bear-selection-btn" onClick={onClearSelection} data-tip="선택 해제">✕</button>
          </div>
        </div>
      )}

      {/* Search */}
      {!trashMode && (
        <div className="bear-search-wrap">
          <div className="bear-search-field">
            <svg className="bear-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
            <input type="text" placeholder="검색" value={search} onChange={(e) => onSearchChange(e.target.value)} className="bear-search-input"
              onKeyDown={(e) => { if (e.key === 'Enter' && onQuickOpen && search.trim()) { e.preventDefault(); e.target.blur(); onQuickOpen('search', search.trim()); } }}
            />
          </div>
          {onQuickOpen && (
            <button className="bear-quickopen-btn" onClick={onQuickOpen} title="빠른 열기 (⌘P)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4l3 3" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Blog (Notes) Section - always expanded */}
      <div className="bear-note-list">
        <div className="bear-tag-header">
          <span className="bear-tag-header-left">
            블로그 (노트)
            <span className="bear-tag-header-count">({documents.length})</span>
          </span>
        </div>
        <div className="bear-section-body">
          {loading ? (
            <div className="bear-note-loading">불러오는 중...</div>
          ) : documents.length === 0 ? (
            <div className="bear-note-empty">
              {trashMode ? '휴지통이 비어있습니다' : search || activeTag ? '결과 없음' : '노트가 없습니다'}
            </div>
          ) : (
            <>
              {pinnedDocs.length > 0 && (
                <>
                  {pinnedDocs.map(doc => renderNoteItem(doc, 0))}
                  {unpinnedDocs.length > 0 && <div className="bear-pin-divider" />}
                </>
              )}
              {unpinnedDocs.map(doc => renderNoteItem(doc, 0))}
            </>
          )}
        </div>
      </div>

      {/* Resize Handle between Notes and Project Docs */}
      {!trashMode && projectDocs && projectDocs.length > 0 && (
        <div className={`bear-tag-resize-handle ${!projectSectionCollapsed ? 'active' : ''}`} onMouseDown={handleProjectResizeStart} onDoubleClick={handleProjectResizeDblClick} ref={projectResizeRef} title="드래그: 크기 조절 / 더블클릭: 최대화·최소화" />
      )}

      {/* Project Docs Section (Collapsible, grouped by machine) */}
      {!trashMode && projectDocs && projectDocs.length > 0 && (() => {
        // 기기별 그룹핑
        const MACHINE_GROUPS = [
          { key: 'macbook', label: '💻 MacBook', icon: '💻', projects: [] },
          { key: 'mini', label: '🤖 미니', icon: '🤖', projects: [] },
          { key: 'dev', label: '🏠 dev', icon: '🏠', projects: [] },
        ];
        // 기기별 프로젝트 분류
        const MACBOOK_PROJECTS = ['dev-workspace', 'lmStudio'];
        const MINI_PROJECTS = ['Myproject-mini', 'ilouli-opentina', 'ilouli-openclaw', 'MemoryStorage'];
        const DEV_PROJECTS = [
          'myproject', 'ilouli-main', 'ilouli-stock', 'ilouli-community',
          'ilouli-admin', 'ilouli-ai', 'ilouli-auth', 'ilouli-family',
          'ilouli-lab', 'ilouli-news', 'ilouli-devteam', 'ilouli-work',
          'ilouli-MarkdownBlog', 'ilouli-StockAnalysis',
        ];
        // 검색어로 프로젝트 문서 필터링
        // 상태 검색 키워드 매핑 (한글/영문/이모지 → doc_status)
        const STATUS_SEARCH_MAP = {
          '녹색': 'ok', '초록': 'ok', 'green': 'ok', '이상없음': 'ok', '동기화': 'ok', '✅': 'ok',
          '노란': 'changed', '노랑': 'changed', 'yellow': 'changed', '변경': 'changed', 'todo': 'changed', '🟡': 'changed',
          '파란': 'review', '파랑': 'review', 'blue': 'review', '검수': 'review', '리뷰': 'review', 'qa': 'review', 'review': 'review', 'fixme': 'review', '🔵': 'review',
          '빨간': 'wip', '빨강': 'wip', 'red': 'wip', '진행중': 'wip', '미완료': 'wip', 'wip': 'wip', '🔴': 'wip',
          '검은': 'unknown', '검정': 'unknown', 'black': 'unknown', '누락': 'unknown', '의문': 'unknown', '⚫': 'unknown',
          '새파일': 'new', '새문서': 'new', 'new': 'new', '최근': 'new', 'recent': 'new', '🆕': 'new',
        };
        const isNewDoc = (doc) => {
          if (!doc.created_at || readDocIds.has(doc.id)) return false;
          const created = new Date(doc.created_at);
          const now = new Date();
          return (now - created) < 3 * 24 * 60 * 60 * 1000; // 3일 이내
        };
        const searchLower = (search || '').toLowerCase().trim();
        const statusFilter = STATUS_SEARCH_MAP[searchLower] || null;
        const filteredProjectDocs = searchLower
          ? projectDocs.map(proj => {
              const filteredDocs = (proj.docs || []).filter(doc =>
                (statusFilter === 'new' && isNewDoc(doc)) ||
                (statusFilter && statusFilter !== 'new' && (doc.doc_status || 'ok') === statusFilter) ||
                (!statusFilter && (
                  (doc.title || '').toLowerCase().includes(searchLower) ||
                  (doc.file_path || '').toLowerCase().includes(searchLower) ||
                  (doc.file_name || '').toLowerCase().includes(searchLower) ||
                  (proj.project || '').toLowerCase().includes(searchLower)
                ))
              );
              if (filteredDocs.length === 0) return null;
              return { ...proj, docs: filteredDocs, doc_count: filteredDocs.length };
            }).filter(Boolean)
          : projectDocs;

        filteredProjectDocs.forEach(proj => {
          if (MACBOOK_PROJECTS.includes(proj.project)) {
            MACHINE_GROUPS[0].projects.push(proj);
          } else if (MINI_PROJECTS.includes(proj.project)) {
            MACHINE_GROUPS[1].projects.push(proj);
          } else if (DEV_PROJECTS.includes(proj.project)) {
            MACHINE_GROUPS[2].projects.push(proj);
          } else {
            MACHINE_GROUPS[2].projects.push(proj);
          }
        });
        // 검색 중이면 결과 있는 그룹만, 아니면 모두 표시
        const activeGroups = searchLower
          ? MACHINE_GROUPS.filter(g => g.projects.length > 0)
          : MACHINE_GROUPS;
        const totalDocs = filteredProjectDocs.reduce((s, p) => s + (p.doc_count || 0), 0);

        if (searchLower && activeGroups.length === 0) return null;

        // 검색 중이면 섹션 강제 펼침
        const isCollapsed = searchLower ? false : projectSectionCollapsed;

        return (
          <div ref={projectSectionRef} className={`bear-project-section bear-resizable-section ${isCollapsed ? 'collapsed' : ''} ${dragging ? 'no-transition' : ''}`} style={!isCollapsed ? { height: `${projectHeight}px` } : undefined}>
            <div className="bear-tag-header bear-tag-header-toggle" onClick={() => setProjectSectionCollapsed(prev => !prev)}>
              <span className="bear-tag-header-left">
                <span className="bear-tree-toggle-inline">{isCollapsed ? '▶' : '▼'}</span>
                프로젝트 문서
                <span className="bear-tag-header-count">({totalDocs})</span>
              </span>
            </div>
            <div className="bear-section-body">
              <div className="bear-project-list">
                {activeGroups.map(group => {
                  const isGroupExpanded = searchLower || expandedProjects.has(`__group__${group.key}`);
                  const groupDocCount = group.projects.reduce((s, p) => s + (p.doc_count || 0), 0);
                  return (
                    <React.Fragment key={group.key}>
                      {/* Machine group header */}
                      <div
                        className="bear-project-group"
                        onClick={() => toggleProjectExpand(`__group__${group.key}`)}
                      >
                        <span className="bear-project-toggle">{isGroupExpanded ? '▼' : '▶'}</span>
                        <span className="bear-project-group-label">{group.label}</span>
                        <span className="bear-tag-count">{groupDocCount}</span>
                      </div>
                      {isGroupExpanded && group.projects.length === 0 && (
                        <div className="bear-project-empty" style={{ paddingLeft: '24px', fontSize: '11px', color: '#aaa' }}>
                          동기화 대기 중
                        </div>
                      )}
                      {isGroupExpanded && group.projects.map(proj => {
                        const isExpanded = searchLower || expandedProjects.has(proj.project);
                        const docCount = proj.doc_count || (proj.docs ? proj.docs.length : 0);
                        return (
                          <React.Fragment key={proj.project}>
                            <div
                              className="bear-project-item"
                              style={{ paddingLeft: '24px' }}
                              onClick={() => toggleProjectExpand(proj.project)}
                            >
                              <span className="bear-project-toggle">{isExpanded ? '▼' : '▶'}</span>
                              <span className="bear-project-icon">📁</span>
                              <span className="bear-project-name-text">{proj.project}</span>
                              <span className="bear-tag-count">{docCount}</span>
                            </div>
                            {isExpanded && proj.docs && proj.docs.length > 0 && (
                              <div className="bear-project-docs">
                                {(() => {
                                  const folders = {};
                                  const rootDocsList = [];
                                  proj.docs.forEach(doc => {
                                    const parts = (doc.file_path || '').split('/');
                                    if (parts.length > 1) {
                                      const folder = parts.slice(0, -1).join('/');
                                      if (!folders[folder]) folders[folder] = [];
                                      folders[folder].push(doc);
                                    } else {
                                      rootDocsList.push(doc);
                                    }
                                  });
                                  return (
                                    <>
                                      {Object.entries(folders).map(([folder, docs]) => (
                                        <React.Fragment key={folder}>
                                          <div className="bear-project-folder" style={{ paddingLeft: '36px' }}>
                                            <span className="bear-project-icon-sm">📂</span>
                                            {folder}/
                                          </div>
                                          {docs.map(doc => (
                                            <div
                                              key={doc.id}
                                              className={`bear-project-doc ${activeProjectDoc?.project === proj.project && activeProjectDoc?.id === doc.id ? 'active' : ''}`}
                                              style={{ paddingLeft: '48px' }}
                                              onClick={(e) => { e.stopPropagation(); markDocRead(doc.id); onProjectDocSelect?.(proj.project, doc.id); }}
                                              title={doc.file_path}
                                            >
                                              <span className="bear-doc-name">{doc.file_name || doc.title}</span>
                                              {isNewDoc(doc) && <span className="bear-new-badge">NEW</span>}
                                              <span className="bear-status-dot-wrap">
                                                <span className={`bear-status-dot bear-status-${doc.doc_status || 'ok'}`} />
                                                <span className="bear-status-dot-tip">{STATUS_TOOLTIPS[doc.doc_status] || STATUS_TOOLTIPS.ok}</span>
                                              </span>
                                            </div>
                                          ))}
                                        </React.Fragment>
                                      ))}
                                      {rootDocsList.map(doc => (
                                        <div
                                          key={doc.id}
                                          className={`bear-project-doc ${activeProjectDoc?.project === proj.project && activeProjectDoc?.id === doc.id ? 'active' : ''}`}
                                          style={{ paddingLeft: '40px' }}
                                          onClick={(e) => { e.stopPropagation(); markDocRead(doc.id); onProjectDocSelect?.(proj.project, doc.id); }}
                                          title={doc.file_path}
                                        >
                                          <span className="bear-doc-name">{doc.file_name || doc.title}</span>
                                          {isNewDoc(doc) && <span className="bear-new-badge">NEW</span>}
                                          <span className="bear-status-dot-wrap">
                                            <span className={`bear-status-dot bear-status-${doc.doc_status || 'ok'}`} />
                                            <span className="bear-status-dot-tip">{STATUS_TOOLTIPS[doc.doc_status] || STATUS_TOOLTIPS.ok}</span>
                                          </span>
                                        </div>
                                      ))}
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                            {isExpanded && (!proj.docs || proj.docs.length === 0) && (
                              <div className="bear-project-empty" style={{ paddingLeft: '40px' }}>문서 없음</div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Resize Handle + Tags (Collapsible, below notes) */}
      {!trashMode && totalTagCount > 0 && onTagSelect && (
        <>
        <div className={`bear-tag-resize-handle ${!tagSectionCollapsed ? 'active' : ''}`} onMouseDown={handleResizeStart} onDoubleClick={handleTagResizeDblClick} ref={resizeRef} title="드래그: 크기 조절 / 더블클릭: 최대화·최소화" />
        <div className={`bear-tag-section bear-resizable-section ${tagSectionCollapsed ? 'collapsed' : ''} ${dragging ? 'no-transition' : ''}`} style={!tagSectionCollapsed ? { height: `${tagHeight}px` } : undefined}>
          <div className="bear-tag-header bear-tag-header-toggle" onClick={() => setTagSectionCollapsed(prev => !prev)}>
            <span className="bear-tag-header-left">
              <span className="bear-tree-toggle-inline">{tagSectionCollapsed ? '▶' : '▼'}</span>
              태그
              <span className="bear-tag-header-count">({totalTagCount})</span>
            </span>
            {!tagSectionCollapsed && (
              <button className="bear-tag-sort-btn" onClick={(e) => { e.stopPropagation(); setTagSortBy(prev => prev === 'count' ? 'name' : 'count'); }}
                title={tagSortBy === 'count' ? '이름순' : '개수순'}>
                {tagSortBy === 'count' ? '개수순' : '이름순'}
              </button>
            )}
          </div>
          <div className="bear-section-body">
            {totalTagCount > 5 && (
              <div className="bear-tag-search-wrap">
                <input type="text" placeholder="태그 검색..." value={tagSearch} onChange={(e) => setTagSearch(e.target.value)} className="bear-tag-search-input" />
              </div>
            )}
            {Object.entries(tagTree).map(([name, node]) => renderTagNode(node, name, 0))}
          </div>
        </div>
        </>
      )}

      {/* Footer */}
      {!trashMode && (
        <div className="bear-sidebar-footer">
          <button className="bear-trash-link" onClick={onTrashToggle} data-tip="휴지통 보기">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
            휴지통{trashCount > 0 ? ` (${trashCount})` : ''}
          </button>
        </div>
      )}
      {trashMode && documents.length > 0 && (
        <div className="bear-sidebar-footer">
          <button className="bear-trash-link danger" onClick={() => { if (window.confirm('휴지통을 비우시겠습니까?')) onEmptyTrash?.(); }} data-tip="휴지통 비우기">휴지통 비우기</button>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && <ContextMenu x={contextMenu.x} y={contextMenu.y} items={contextMenu.items} onClose={() => setContextMenu(null)} />}

      {/* Rename Dialog */}
      {renameDialog && (
        <div className="bear-modal-overlay" onClick={() => setRenameDialog(null)}>
          <div className="bear-modal-dialog" onClick={e => e.stopPropagation()}>
            <div className="bear-modal-title">태그 이름 변경</div>
            <div className="bear-modal-body">
              <div className="bear-modal-label">현재: #{renameDialog.tag}</div>
              <input ref={renameInputRef} type="text" className="bear-modal-input" value={renameDialog.value}
                onChange={e => setRenameDialog({ ...renameDialog, value: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter') handleRenameSubmit(); if (e.key === 'Escape') setRenameDialog(null); }}
                placeholder="새 이름" />
            </div>
            <div className="bear-modal-actions">
              <button className="bear-modal-btn cancel" onClick={() => setRenameDialog(null)}>취소</button>
              <button className="bear-modal-btn primary" onClick={handleRenameSubmit}>변경</button>
            </div>
          </div>
        </div>
      )}

      {/* Color Picker */}
      {colorPicker && (
        <div className="bear-modal-overlay" onClick={() => setColorPicker(null)}>
          <div className="bear-modal-dialog bear-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="bear-modal-title">#{colorPicker.tag} 색상</div>
            <div className="bear-color-grid">
              {TAG_COLORS.map(color => (
                <button key={color} className={`bear-color-swatch ${getTagColor(colorPicker.tag) === color ? 'active' : ''}`}
                  style={{ background: color }} onClick={() => { onTagColor?.(colorPicker.tag, color); setColorPicker(null); }} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Merge Dialog */}
      {mergeDialog && (
        <div className="bear-modal-overlay" onClick={() => setMergeDialog(null)}>
          <div className="bear-modal-dialog" onClick={e => e.stopPropagation()}>
            <div className="bear-modal-title">태그 병합</div>
            <div className="bear-modal-body">
              <div className="bear-modal-label">#{mergeDialog.sourceTag} → 다른 태그로 병합</div>
              <select className="bear-modal-input" value={mergeTarget} onChange={e => setMergeTarget(e.target.value)}>
                <option value="">대상 태그 선택...</option>
                {tagFlat.filter(([tag]) => tag !== mergeDialog.sourceTag).map(([tag, count]) => (
                  <option key={tag} value={tag}>#{tag} ({count})</option>
                ))}
              </select>
            </div>
            <div className="bear-modal-actions">
              <button className="bear-modal-btn cancel" onClick={() => setMergeDialog(null)}>취소</button>
              <button className="bear-modal-btn primary" onClick={handleMergeSubmit} disabled={!mergeTarget}>병합</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
